import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner, Table } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function CompetitiveAnalysis() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('input'); // input, analyzing, results, select-existing, identifying-gaps, gaps, prioritize, roadmap, summary, complete
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Competitor data
  const [userUrl, setUserUrl] = useState('');
  const [competitors, setCompetitors] = useState([
    { name: '', url: '' },
    { name: '', url: '' },
    { name: '', url: '' },
    { name: '', url: '' },
    { name: '', url: '' }
  ]);

  // Analysis results
  const [analysisResults, setAnalysisResults] = useState([]);
  const [userFeatures, setUserFeatures] = useState([]);
  const [userBenefits, setUserBenefits] = useState([]);
  const [userPricing, setUserPricing] = useState('');
  const [userPositioning, setUserPositioning] = useState('');

  // Feature gaps and roadmap
  const [existingFeatures, setExistingFeatures] = useState([]); // Features user already has
  const [featureGaps, setFeatureGaps] = useState([]);
  const [prioritizedFeatures, setPrioritizedFeatures] = useState([]);
  const [roadmapFeatures, setRoadmapFeatures] = useState([]);

  // Analysis tracking
  const [analysisCount, setAnalysisCount] = useState(0);
  const [lastCompetitors, setLastCompetitors] = useState([]);

  // Custom features/benefits added while reviewing
  const [customFeature, setCustomFeature] = useState('');
  const [customBenefit, setCustomBenefit] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Try to prepopulate userUrl from saved website/sales page
    try {
      // First try sales_page_url, then fall back to website_url
      const salesPageResponse = await fetch('/api/business/field/sales_page_url', {
        credentials: 'include'
      });
      if (salesPageResponse.ok) {
        const salesData = await salesPageResponse.json();
        if (salesData.field_value) {
          setUserUrl(salesData.field_value);
        } else {
          // Fall back to website_url
          const websiteResponse = await fetch('/api/business/field/website_url', {
            credentials: 'include'
          });
          if (websiteResponse.ok) {
            const websiteData = await websiteResponse.json();
            if (websiteData.field_value) {
              setUserUrl(websiteData.field_value);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading saved URL:', error);
    }

    // Check if user has saved analysis
    try {
      const response = await fetch('/api/competitive-analysis/get-saved', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        // If we have saved analysis, load it and show results
        if (data.analysis && data.analysis.length > 0) {
          setAnalysisResults(data.analysis);
          setUserFeatures(data.user_features || []);
          setUserBenefits(data.user_benefits || []);
          setUserPricing(data.user_pricing || '');
          setUserPositioning(data.user_positioning || '');
          setStage('results');
        }
      }
    } catch (error) {
      console.error('Error loading saved analysis:', error);
      // If there's an error, just stay on input stage
    }

    setLoading(false);
  };

  const handleCompetitorChange = (index, field, value) => {
    const newCompetitors = [...competitors];
    newCompetitors[index][field] = value;
    setCompetitors(newCompetitors);
  };

  const handleAnalyze = async () => {
    // Check if they've hit the limit
    if (analysisCount >= 3) {
      setError('You\'ve reached the maximum of 3 competitive analyses. Please proceed with your current analysis or start over to modify competitors.');
      return;
    }

    // Validate - need at least 3 competitors with URLs
    const filledCompetitors = competitors.filter(c => c.url.trim() !== '');
    if (filledCompetitors.length < 3) {
      setError('Please enter at least 3 competitor URLs');
      return;
    }

    // Save competitors for potential repopulation
    setLastCompetitors([...competitors]);

    setAnalyzing(true);
    setError('');
    setStage('analyzing');

    try {
      // Send to backend for analysis
      const response = await fetch('/api/competitive-analysis/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          user_url: userUrl,
          competitors: filledCompetitors
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysisResults(data.analysis || []);
        setUserFeatures(data.user_features || []);
        setUserBenefits(data.user_benefits || []);
        setUserPricing(data.user_pricing || '');
        setUserPositioning(data.user_positioning || '');
        setAnalysisCount(analysisCount + 1);
        setStage('results');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to analyze competitors');
        setStage('input');
      }
    } catch (error) {
      setError('Error analyzing competitors: ' + error.message);
      setStage('input');
    }

    setAnalyzing(false);
  };

  const handleStartOver = () => {
    // Pre-populate with last competitors if available, otherwise blank
    const resetCompetitors = lastCompetitors.length > 0 ? [...lastCompetitors] : [
      { name: '', url: '' },
      { name: '', url: '' },
      { name: '', url: '' },
      { name: '', url: '' },
      { name: '', url: '' }
    ];

    setCompetitors(resetCompetitors);
    setAnalysisResults([]);
    setUserFeatures([]);
    setUserBenefits([]);
    setExistingFeatures([]);
    setFeatureGaps([]);
    setPrioritizedFeatures([]);
    setRoadmapFeatures([]);
    setError('');
    setStage('input');
    // Don't reset analysisCount - keep tracking across restarts
  };

  const handleToggleExistingFeature = (feature) => {
    const exists = existingFeatures.find(f => f.feature === feature.feature && f.competitor === feature.competitor);
    if (exists) {
      // Remove it
      setExistingFeatures(existingFeatures.filter(f => !(f.feature === feature.feature && f.competitor === feature.competitor)));
    } else {
      // Add it
      setExistingFeatures([...existingFeatures, feature]);
    }
  };

  const handleAddCustomFeature = () => {
    if (customFeature.trim()) {
      const newFeature = {
        feature: customFeature.trim(),
        type: 'feature',
        competitor: 'Your Own'
      };
      setExistingFeatures([...existingFeatures, newFeature]);
      setCustomFeature('');
    }
  };

  const handleAddCustomBenefit = () => {
    if (customBenefit.trim()) {
      const newBenefit = {
        feature: customBenefit.trim(),
        type: 'benefit',
        competitor: 'Your Own'
      };
      setExistingFeatures([...existingFeatures, newBenefit]);
      setCustomBenefit('');
    }
  };

  const handleIdentifyGaps = async () => {
    setAnalyzing(true);
    setError('');

    // Simple approach: gaps are just the features user DIDN'T check
    const allCompetitorFeatures = [];

    analysisResults.forEach(comp => {
      (comp.features || []).forEach(feature => {
        allCompetitorFeatures.push({
          feature: feature,
          type: 'feature',
          competitor: comp.name,
          competitors: [comp.name]
        });
      });
      (comp.benefits || []).forEach(benefit => {
        allCompetitorFeatures.push({
          feature: benefit,
          type: 'benefit',
          competitor: comp.name,
          competitors: [comp.name]
        });
      });
    });

    // Filter out the ones user already checked
    const gaps = allCompetitorFeatures.filter(item => {
      const isChecked = existingFeatures.some(
        existing => existing.feature === item.feature && existing.competitor === item.competitor
      );
      return !isChecked;
    });

    // Remove duplicates by feature name
    const uniqueGaps = [];
    const seen = new Set();
    gaps.forEach(gap => {
      if (!seen.has(gap.feature)) {
        seen.add(gap.feature);
        uniqueGaps.push({
          feature: gap.feature,
          description: `${gap.type === 'benefit' ? 'Benefit' : 'Feature'} from competitors`,
          competitors: gaps
            .filter(g => g.feature === gap.feature)
            .map(g => g.competitor)
            .filter((v, i, a) => a.indexOf(v) === i) // unique competitors
        });
      }
    });

    // Save existing features immediately so they appear in Features & Benefits page
    try {
      const response = await fetch('/api/competitive-analysis/save-existing-features', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_features: userFeatures,
          user_benefits: userBenefits,
          existing_features: existingFeatures
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(`Failed to save features: ${errorData.error || 'Unknown error'}`);
        setAnalyzing(false);
        return;
      }
    } catch (err) {
      setError('Error saving features: ' + err.message);
      setAnalyzing(false);
      return;
    }

    setFeatureGaps(uniqueGaps);
    setAnalyzing(false);
    setStage('gaps');
  };

  const handlePrioritizeFeature = (index, priority) => {
    const newGaps = [...featureGaps];
    newGaps[index].priority = priority;
    setFeatureGaps(newGaps);
  };

  const handleContinueToPrioritize = () => {
    // Move features marked as Must Have or Nice to Have to prioritization
    const toPrioritize = featureGaps.filter(gap => gap.priority && gap.priority !== 'skip');
    setPrioritizedFeatures(toPrioritize.map(gap => ({
      ...gap,
      roadmapPriority: '',
      targetQuarter: '',
      category: '',
      notes: ''
    })));
    setStage('prioritize');
  };

  const handleRoadmapChange = (index, field, value) => {
    const newFeatures = [...prioritizedFeatures];
    newFeatures[index][field] = value;
    setPrioritizedFeatures(newFeatures);
  };

  const handleContinueToSummary = () => {
    setRoadmapFeatures(prioritizedFeatures);
    setStage('summary');
  };

  const handleSaveRoadmap = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/competitive-analysis/save-roadmap', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          analysis: analysisResults,
          user_features: userFeatures,
          user_benefits: userBenefits,
          existing_features: existingFeatures, // Features user marked as "already have"
          roadmap: roadmapFeatures
        })
      });

      if (response.ok) {
        setStage('complete');
        setTimeout(() => {
          navigate('/dashboard/business-model');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save roadmap');
      }
    } catch (error) {
      setError('Error saving roadmap: ' + error.message);
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  // Stage 1: Input
  if (stage === 'input') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={9}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">Competitive Intelligence</h2>

                <p className="text-muted mb-4">
                  Let's analyze your competition to understand the market and identify opportunities.
                </p>

                <Alert variant="info" className="mb-4">
                  <strong>What are real competitors?</strong><br />
                  Businesses selling similar solutions to the same target market, NOT just similar products.<br /><br />
                  <em>Example:</em> If you sell meal planning for digital nomads, your competitor is OTHER meal planning apps for travelers, NOT general meal planning apps for families.
                </Alert>

                {analysisCount > 0 && (
                  <Alert variant={analysisCount >= 3 ? 'warning' : 'secondary'} className="mb-3">
                    <strong>Analyses Completed:</strong> {analysisCount} of 3
                    {analysisCount >= 3 && ' (Maximum reached - modify your current competitors below if needed)'}
                  </Alert>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                <Form>
                  <Card className="mb-4 bg-light">
                    <Card.Body>
                      <h5>Your Sales Page (optional)</h5>
                      <Form.Group>
                        <Form.Label>URL</Form.Label>
                        <Form.Control
                          type="url"
                          value={userUrl}
                          onChange={(e) => setUserUrl(e.target.value)}
                          placeholder="https://yoursite.com"
                        />
                        <Form.Text className="text-muted">
                          We'll analyze yours too if provided
                        </Form.Text>
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  {competitors.map((comp, index) => (
                    <Card key={index} className={`mb-3 ${index < 3 ? 'border-primary' : 'border-secondary'}`}>
                      <Card.Body>
                        <h6>Competitor {index + 1} {index < 3 ? '(Required)' : '(Optional)'}</h6>
                        <Row>
                          <Col md={5}>
                            <Form.Group>
                              <Form.Label>Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={comp.name}
                                onChange={(e) => handleCompetitorChange(index, 'name', e.target.value)}
                                placeholder="Company name"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={7}>
                            <Form.Group>
                              <Form.Label>URL</Form.Label>
                              <Form.Control
                                type="url"
                                value={comp.url}
                                onChange={(e) => handleCompetitorChange(index, 'url', e.target.value)}
                                placeholder="https://competitor.com"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}

                  <Alert variant="secondary">
                    💡 <strong>TIP:</strong> Include a mix of direct competitors and aspirational competitors (where you want to be).
                  </Alert>

                  <div className="d-flex justify-content-between mt-4">
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/dashboard')}
                    >
                      Save for Later
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleAnalyze}
                      disabled={analyzing}
                    >
                      Analyze Competitors
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 2: Analyzing
  if (stage === 'analyzing') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5 text-center">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h4>🤖 Analyzing Competitor Pages...</h4>
                <p className="text-muted mb-4">
                  This may take 60-90 seconds. We're:
                </p>
                <ul className="text-left" style={{ maxWidth: '400px', margin: '0 auto' }}>
                  <li>✓ Fetching all competitor pages</li>
                  <li>✓ Analyzing features and benefits</li>
                  <li>✓ Extracting pricing information</li>
                  <li>✓ Identifying positioning strategies</li>
                  <li>✓ Comparing offers</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 3: Results
  if (stage === 'results') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={12} lg={11}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">Competitive Analysis Results</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="success" className="mb-4">
                  Analysis complete! Below is what we found from {analysisResults.length} competitor{analysisResults.length !== 1 ? 's' : ''}.
                </Alert>

                {analysisResults.map((comp, index) => (
                  <Card key={index} className="mb-4 border-info">
                    <Card.Header className="bg-info text-white">
                      <strong>{comp.name || `Competitor ${index + 1}`}</strong>
                      <small className="ms-2">{comp.url}</small>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <h6>Features ({comp.features?.length || 0})</h6>
                          <ul className="small">
                            {comp.features?.slice(0, 5).map((f, i) => (
                              <li key={i}>{f}</li>
                            ))}
                            {comp.features?.length > 5 && <li><em>...and {comp.features.length - 5} more</em></li>}
                          </ul>
                        </Col>
                        <Col md={6}>
                          <h6>Benefits ({comp.benefits?.length || 0})</h6>
                          <ul className="small">
                            {comp.benefits?.slice(0, 5).map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                            {comp.benefits?.length > 5 && <li><em>...and {comp.benefits.length - 5} more</em></li>}
                          </ul>
                        </Col>
                      </Row>
                      <Row className="mt-3">
                        <Col md={6}>
                          <h6>Pricing</h6>
                          <p className="small">
                            {typeof comp.pricing === 'object' && comp.pricing.description
                              ? comp.pricing.description
                              : comp.pricing || 'Not found'}
                          </p>
                        </Col>
                        <Col md={6}>
                          <h6>Positioning</h6>
                          <p className="small">{comp.positioning || 'Not found'}</p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}

                {userFeatures.length > 0 && (
                  <Card className="mb-4 border-success">
                    <Card.Header className="bg-success text-white">
                      <strong>Your Features & Benefits</strong>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <h6>Your Features ({userFeatures.length})</h6>
                          <ul className="small">
                            {userFeatures.map((f, i) => (
                              <li key={i}>{f}</li>
                            ))}
                          </ul>
                        </Col>
                        <Col md={6}>
                          <h6>Your Benefits ({userBenefits.length})</h6>
                          <ul className="small">
                            {userBenefits.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {/* Positioning Comparison Table */}
                <Card className="mb-4 border-warning">
                  <Card.Header className="bg-warning">
                    <strong>Positioning & Pricing Comparison</strong>
                  </Card.Header>
                  <Card.Body>
                    <p className="text-muted small mb-3">
                      How you stack up against the competition at a glance.
                    </p>
                    <div className="table-responsive">
                      <Table bordered hover size="sm">
                        <thead className="table-light">
                          <tr>
                            <th style={{width: '20%'}}>Company</th>
                            <th style={{width: '40%'}}>Positioning</th>
                            <th style={{width: '40%'}}>Pricing</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="table-success">
                            <td><strong>YOU</strong></td>
                            <td className="small">{userPositioning || <em className="text-muted">Not analyzed</em>}</td>
                            <td className="small">{userPricing || <em className="text-muted">Not analyzed</em>}</td>
                          </tr>
                          {analysisResults.map((comp, index) => (
                            <tr key={index}>
                              <td>{comp.name || `Competitor ${index + 1}`}</td>
                              <td className="small">{comp.positioning || <em className="text-muted">Not found</em>}</td>
                              <td className="small">
                                {typeof comp.pricing === 'object' && comp.pricing.description
                                  ? comp.pricing.description
                                  : comp.pricing || <em className="text-muted">Not found</em>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={handleStartOver}
                  >
                    Start Over
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setStage('select-existing')}
                  >
                    Continue to Feature Selection →
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 4: Select Existing Features
  if (stage === 'select-existing') {
    // Compile all competitor features
    const allCompetitorFeatures = [];
    analysisResults.forEach(comp => {
      (comp.features || []).forEach(feature => {
        allCompetitorFeatures.push({
          feature: feature,
          type: 'feature',
          competitor: comp.name
        });
      });
      (comp.benefits || []).forEach(benefit => {
        allCompetitorFeatures.push({
          feature: benefit,
          type: 'benefit',
          competitor: comp.name
        });
      });
    });

    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">Select Features You Already Have</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="info" className="mb-4">
                  <strong>Before identifying gaps, let's see what you already have!</strong><br />
                  Check off any competitor features/benefits that YOUR product also has - even if they're not mentioned on your sales page or worded differently.
                </Alert>

                <p className="text-muted small mb-4">
                  Selected: {existingFeatures.length} feature{existingFeatures.length !== 1 ? 's' : ''}
                </p>

                {analysisResults.map((comp, compIndex) => (
                  <Card key={compIndex} className="mb-4">
                    <Card.Header className="bg-light">
                      <strong>{comp.name}</strong>
                    </Card.Header>
                    <Card.Body>
                      {(comp.features && comp.features.length > 0) && (
                        <div className="mb-3">
                          <h6 className="text-muted small">FEATURES:</h6>
                          {comp.features.map((feature, idx) => {
                            const featureObj = { feature, type: 'feature', competitor: comp.name };
                            const isSelected = existingFeatures.some(f => f.feature === feature && f.competitor === comp.name);
                            return (
                              <Form.Check
                                key={idx}
                                type="checkbox"
                                id={`feature-${compIndex}-${idx}`}
                                label={feature}
                                checked={isSelected}
                                onChange={() => handleToggleExistingFeature(featureObj)}
                                className="mb-2"
                              />
                            );
                          })}
                        </div>
                      )}

                      {(comp.benefits && comp.benefits.length > 0) && (
                        <div>
                          <h6 className="text-muted small">BENEFITS:</h6>
                          {comp.benefits.map((benefit, idx) => {
                            const benefitObj = { feature: benefit, type: 'benefit', competitor: comp.name };
                            const isSelected = existingFeatures.some(f => f.feature === benefit && f.competitor === comp.name);
                            return (
                              <Form.Check
                                key={idx}
                                type="checkbox"
                                id={`benefit-${compIndex}-${idx}`}
                                label={benefit}
                                checked={isSelected}
                                onChange={() => handleToggleExistingFeature(benefitObj)}
                                className="mb-2"
                              />
                            );
                          })}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}

                {/* Add Your Own Features/Benefits */}
                <Card className="mb-4 border-primary">
                  <Card.Header className="bg-primary text-white">
                    <strong>💡 Add Your Own Features or Benefits</strong>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="info" className="mb-3">
                      <small>
                        <strong>Reviewing competitors jog your memory?</strong> Add your own features/benefits here that you thought of while reviewing.
                      </small>
                    </Alert>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Label>Add a Feature</Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="text"
                            placeholder="E.g., 'Unlimited revisions'"
                            value={customFeature}
                            onChange={(e) => setCustomFeature(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddCustomFeature();
                              }
                            }}
                          />
                          <Button
                            variant="primary"
                            onClick={handleAddCustomFeature}
                            disabled={!customFeature.trim()}
                          >
                            Add
                          </Button>
                        </div>
                      </Col>

                      <Col md={6} className="mb-3">
                        <Form.Label>Add a Benefit</Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="text"
                            placeholder="E.g., 'Save 10 hours per week'"
                            value={customBenefit}
                            onChange={(e) => setCustomBenefit(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddCustomBenefit();
                              }
                            }}
                          />
                          <Button
                            variant="primary"
                            onClick={handleAddCustomBenefit}
                            disabled={!customBenefit.trim()}
                          >
                            Add
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    {/* Show custom items that were added */}
                    {existingFeatures.filter(f => f.competitor === 'Your Own').length > 0 && (
                      <div className="mt-3 pt-3 border-top">
                        <h6 className="text-muted small mb-2">YOUR CUSTOM ADDITIONS:</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {existingFeatures.filter(f => f.competitor === 'Your Own').map((item, idx) => (
                            <span key={idx} className="badge bg-success">
                              {item.type === 'feature' ? '⭐' : '✨'} {item.feature}
                              <button
                                type="button"
                                className="btn-close btn-close-white ms-2"
                                style={{ fontSize: '0.6rem' }}
                                onClick={() => {
                                  setExistingFeatures(existingFeatures.filter(f => f !== item));
                                }}
                                aria-label="Remove"
                              ></button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('results')}
                  >
                    ← Back to Results
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleIdentifyGaps}
                    disabled={analyzing}
                  >
                    {analyzing ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving Features...
                      </>
                    ) : (
                      'Identify Missing Features →'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 5: Identifying Gaps (AI Processing)
  if (stage === 'identifying-gaps') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5 text-center">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h4>🔎 Identifying True Feature Gaps...</h4>
                <p className="text-muted mb-4">
                  AI is analyzing what your competitors have that you DON'T have (excluding features you marked as already having)...
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 6: Feature Gaps
  if (stage === 'gaps') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">Feature Gaps Identified</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="info" className="mb-4">
                  <strong>Prioritize each feature gap:</strong><br />
                  • <strong>Must Have:</strong> Critical features you need to compete<br />
                  • <strong>Nice to Have:</strong> Would improve your offering but not critical<br />
                  • <strong>Skip:</strong> Not relevant to your market or strategy
                </Alert>

                {featureGaps.length === 0 ? (
                  <Alert variant="success">
                    Great news! We didn't find any significant feature gaps. You're competitive with the analyzed competitors.
                  </Alert>
                ) : (
                  <>
                    {featureGaps.map((gap, index) => (
                      <Card key={index} className="mb-3">
                        <Card.Body>
                          <Row>
                            <Col md={7}>
                              <h6>{gap.feature}</h6>
                              <p className="small text-muted mb-2">{gap.description}</p>
                              <p className="small">
                                <strong>Found in:</strong> {gap.competitors.join(', ')}
                              </p>
                            </Col>
                            <Col md={5}>
                              <Form.Group>
                                <Form.Label className="small">Priority</Form.Label>
                                <div className="d-grid gap-2">
                                  <Button
                                    variant={gap.priority === 'must-have' ? 'danger' : 'outline-danger'}
                                    size="sm"
                                    onClick={() => handlePrioritizeFeature(index, 'must-have')}
                                  >
                                    Must Have
                                  </Button>
                                  <Button
                                    variant={gap.priority === 'nice-to-have' ? 'warning' : 'outline-warning'}
                                    size="sm"
                                    onClick={() => handlePrioritizeFeature(index, 'nice-to-have')}
                                  >
                                    Nice to Have
                                  </Button>
                                  <Button
                                    variant={gap.priority === 'skip' ? 'secondary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => handlePrioritizeFeature(index, 'skip')}
                                  >
                                    Skip
                                  </Button>
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}

                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setStage('results')}
                      >
                        ← Back to Results
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleContinueToPrioritize}
                        disabled={!featureGaps.some(g => g.priority && g.priority !== 'skip')}
                      >
                        Continue to Roadmap Planning →
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 7: Roadmap Planning
  if (stage === 'prioritize' || stage === 'roadmap') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">Roadmap Planning</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="info" className="mb-4">
                  Assign priorities and target quarters for each feature you want to build.
                </Alert>

                {prioritizedFeatures.map((feature, index) => (
                  <Card key={index} className="mb-3">
                    <Card.Body>
                      <h6 className="mb-3">
                        <span className={`badge ${feature.priority === 'must-have' ? 'bg-danger' : 'bg-warning'} me-2`}>
                          {feature.priority === 'must-have' ? 'Must Have' : 'Nice to Have'}
                        </span>
                        {feature.feature}
                      </h6>
                      <p className="small text-muted mb-3">{feature.description}</p>

                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="small">Roadmap Priority</Form.Label>
                            <Form.Select
                              size="sm"
                              value={feature.roadmapPriority}
                              onChange={(e) => handleRoadmapChange(index, 'roadmapPriority', e.target.value)}
                            >
                              <option value="">Select...</option>
                              <option value="high">High</option>
                              <option value="medium">Medium</option>
                              <option value="low">Low</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="small">Target Quarter</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Q1 2025"
                              value={feature.targetQuarter}
                              onChange={(e) => handleRoadmapChange(index, 'targetQuarter', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="small">Category</Form.Label>
                            <Form.Select
                              size="sm"
                              value={feature.category}
                              onChange={(e) => handleRoadmapChange(index, 'category', e.target.value)}
                            >
                              <option value="">Select...</option>
                              <option value="core-functionality">Core Functionality</option>
                              <option value="user-experience">User Experience</option>
                              <option value="integrations">Integrations</option>
                              <option value="performance">Performance</option>
                              <option value="security">Security</option>
                              <option value="other">Other</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="small">Status</Form.Label>
                            <Form.Select
                              size="sm"
                              value={feature.status || 'roadmap'}
                              onChange={(e) => handleRoadmapChange(index, 'status', e.target.value)}
                            >
                              <option value="roadmap">Roadmap</option>
                              <option value="considering">Considering</option>
                              <option value="not_adopting">Not Adopting</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group>
                        <Form.Label className="small">Notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          placeholder="Implementation notes, dependencies, etc."
                          value={feature.notes}
                          onChange={(e) => handleRoadmapChange(index, 'notes', e.target.value)}
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                ))}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('gaps')}
                  >
                    ← Back to Gaps
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleContinueToSummary}
                  >
                    Review Summary →
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 8: Summary
  if (stage === 'summary') {
    // Group by quarter
    const byQuarter = {};
    roadmapFeatures.forEach(feature => {
      const quarter = feature.targetQuarter || 'Unscheduled';
      if (!byQuarter[quarter]) {
        byQuarter[quarter] = [];
      }
      byQuarter[quarter].push(feature);
    });

    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">Product Roadmap Summary</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="success" className="mb-4">
                  Review your product roadmap before saving.
                </Alert>

                {Object.keys(byQuarter).sort().map((quarter) => (
                  <div key={quarter} className="mb-4">
                    <h5 className="mb-3">{quarter}</h5>
                    {byQuarter[quarter].map((feature, index) => (
                      <Card key={index} className="mb-2 border-start border-4" style={{
                        borderLeftColor: feature.roadmapPriority === 'high' ? '#dc3545' :
                                       feature.roadmapPriority === 'medium' ? '#ffc107' : '#6c757d'
                      }}>
                        <Card.Body className="py-2">
                          <Row className="align-items-center">
                            <Col md={6}>
                              <strong>{feature.feature}</strong>
                              <span className={`badge ms-2 ${feature.priority === 'must-have' ? 'bg-danger' : 'bg-warning'}`}>
                                {feature.priority === 'must-have' ? 'Must Have' : 'Nice to Have'}
                              </span>
                            </Col>
                            <Col md={2} className="small text-muted">
                              {feature.category || 'Uncategorized'}
                            </Col>
                            <Col md={2} className="small text-muted">
                              Priority: {feature.roadmapPriority || 'Not set'}
                            </Col>
                            <Col md={2} className="small text-muted">
                              Status: {feature.status || 'roadmap'}
                            </Col>
                          </Row>
                          {feature.notes && (
                            <Row>
                              <Col className="small text-muted mt-1">
                                <em>{feature.notes}</em>
                              </Col>
                            </Row>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                ))}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('roadmap')}
                  >
                    ← Back to Edit
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleSaveRoadmap}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Roadmap & Complete'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 9: Complete
  if (stage === 'complete') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow border-success">
              <Card.Body className="p-4 text-center">
                <h2 className="mb-4">Product Roadmap Saved!</h2>
                <Alert variant="success">
                  Your competitive analysis and product roadmap have been saved successfully.
                </Alert>
                <p className="text-muted">
                  Returning to Dashboard...
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default CompetitiveAnalysis;
